/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Edit2, Package, RefreshCw, AlertTriangle, Tags, Layers } from 'lucide-react';
import { ADMIN } from '@/constants/testIds';
import { StoreFilter } from '@/components/StoreFilter';
import { usePlatform } from '@/hooks/usePlatform';
import { toast } from 'sonner';

const emptyForm = {
  store_id: '',
  name: '',
  description: '',
  image_url: '',
  vitrine_name: '',
  vitrine_description: '',
  vitrine_image_url: '',
  price: '',
  sku: '',
  shopify_variant_id: '',
  shopify_product_id: '',
  vitrine_url: '',
  vitrine_product_id: '',
  bogo_enabled: false,
  bogo_offer_type: 'second_free',
  bogo_discount_percent: 50,
  bogo_scope: 'same_product',
  active: true,
};

export default function Products() {
  const { matchStore, label: platformLabel } = usePlatform();
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [storeFilter, setStoreFilter] = useState(''); // '' = toutes les boutiques

  // Re-import from Shopify (wipe + refresh)
  const [reimportOpen, setReimportOpen] = useState(false);
  const [reimportStoreId, setReimportStoreId] = useState('');
  const [reimportWipe, setReimportWipe] = useState(true);
  const [reimportRunning, setReimportRunning] = useState(false);
  const [reimportResult, setReimportResult] = useState(null);

  // Bulk vitrine-name editor
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Per-variant vitrine editor (multi-price products)
  const [variantsProduct, setVariantsProduct] = useState(null);
  const [variantRows, setVariantRows] = useState([]);
  const [variantsSaving, setVariantsSaving] = useState(false);

  const openVariants = (p) => {
    setVariantsProduct(p);
    setVariantRows((p.variants || []).map((v) => ({
      variant_id: String(v.variant_id || ''),
      title: v.title || '',
      price: v.price,
      sku: v.sku || '',
      vitrine_name: v.vitrine_name || '',
      vitrine_url: v.vitrine_url || '',
      vitrine_image_url: v.vitrine_image_url || '',
      vitrine_product_id: v.vitrine_product_id || '',
    })));
  };
  const updateVariantRow = (idx, field, value) => {
    setVariantRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const saveVariants = async () => {
    setVariantsSaving(true);
    try {
      await api.put(`/products/${variantsProduct.id}/variants`, {
        variants: variantRows.map((r) => ({
          variant_id: r.variant_id,
          vitrine_name: r.vitrine_name,
          vitrine_url: r.vitrine_url,
          vitrine_image_url: r.vitrine_image_url,
          vitrine_product_id: r.vitrine_product_id,
        })),
      });
      toast.success('Variantes mises à jour');
      setVariantsProduct(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setVariantsSaving(false);
    }
  };

  const openBulk = () => {
    setBulkRows(products.map((p) => ({
      id: p.id,
      name: p.name,
      store_id: p.store_id,
      vitrine_name: p.vitrine_name || '',
      vitrine_url: p.vitrine_url || '',
      vitrine_product_id: p.vitrine_product_id || '',
    })));
    setBulkOpen(true);
  };

  const updateBulkRow = (idx, field, value) => {
    setBulkRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const saveBulk = async () => {
    setBulkSaving(true);
    try {
      const items = bulkRows.map((r) => ({
        product_id: r.id,
        vitrine_name: r.vitrine_name,
        vitrine_url: r.vitrine_url,
        vitrine_product_id: r.vitrine_product_id,
      }));
      const res = await api.put('/products/bulk-vitrine', { items });
      toast.success(`${res.data.updated} produit(s) mis à jour`);
      setBulkOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setBulkSaving(false);
    }
  };

  const load = async () => {
    const [p, s] = await Promise.all([api.get('/products'), api.get('/stores')]);
    setProducts(p.data);
    setStores(s.data);
  };
  useEffect(() => { load(); }, []);

  const openReimport = () => {
    setReimportStoreId(platformStores[0]?.id || '');
    setReimportWipe(true);
    setReimportResult(null);
    setReimportOpen(true);
  };

  const runReimport = async () => {
    setReimportRunning(true);
    setReimportResult(null);
    try {
      const r = await api.post('/products/reimport-shopify', {
        store_id: reimportStoreId || '',
        wipe: reimportWipe,
      });
      setReimportResult(r.data);
      const s = r.data.summary || {};
      const plat = stores.find((x) => x.id === reimportStoreId)?.platform === 'woocommerce' ? 'WooCommerce' : 'Shopify';
      const msg = reimportWipe
        ? `${s.total_deleted || 0} supprimé(s), ${s.total_imported || 0} importé(s)`
        : `${s.total_imported || 0} produit(s) synchronisé(s)`;
      toast.success(`${plat} : ${msg}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de réimport');
    } finally {
      setReimportRunning(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, store_id: platformStores[0]?.id || '' });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyForm, ...p, price: String(p.price) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.store_id || !form.name || !form.price) {
      toast.error('Boutique, nom et prix requis');
      return;
    }
    const price = parseFloat(form.price);
    if (isNaN(price) || price <= 0) {
      toast.error('Prix invalide');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price, bogo_discount_percent: parseFloat(form.bogo_discount_percent) || 0 };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success('Produit mis à jour');
      } else {
        await api.post('/products', payload);
        toast.success('Produit créé');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Supprimer ce produit ?')) return;
    await api.delete(`/products/${id}`);
    toast.success('Produit supprimé');
    await load();
  };

  const storeName = (id) => stores.find((s) => s.id === id)?.name || '—';

  const platformStores = stores.filter(matchStore);
  const platformStoreIds = new Set(platformStores.map((s) => s.id));
  const platformProducts = products.filter((p) => platformStoreIds.has(p.store_id));
  const visible = storeFilter ? platformProducts.filter((p) => p.store_id === storeFilter) : platformProducts;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-5 mb-8">
        <div>
          <div className="overline mb-2">— Catalogue</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Produits{platformLabel ? ` · ${platformLabel}` : ''}</h1>
          <p className="text-zinc-600 mt-2">Définissez les articles à proposer dans votre checkout externe.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={openBulk}
            disabled={products.length === 0}
            data-testid="bulk-vitrine-button"
            className="rounded-md"
          >
            <Tags className="h-4 w-4 mr-2" /> Noms vitrine (en masse)
          </Button>
          <Button
            variant="outline"
            onClick={openReimport}
            disabled={platformStores.length === 0}
            data-testid="reimport-shopify-button"
            className="rounded-md"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Réimporter les produits
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              disabled={platformStores.length === 0}
              data-testid={ADMIN.addProductButton}
              className="btn-primary rounded-md"
            >
              <Plus className="h-4 w-4 mr-2" /> Nouveau produit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? 'Modifier le produit' : 'Nouveau produit'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label className="text-xs">BOUTIQUE</Label>
                <Select value={form.store_id} onValueChange={(v) => setForm({ ...form, store_id: v })}>
                  <SelectTrigger
                    data-testid={ADMIN.productStoreSelect}
                    className="mt-1.5 h-10 rounded-md"
                  >
                    <SelectValue placeholder="Choisir une boutique" />
                  </SelectTrigger>
                  <SelectContent>
                    {platformStores.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">NOM PRODUIT (côté Shopify — vu par le client venant de Shopify)</Label>
                <Input
                  value={form.name}
                  data-testid={ADMIN.productNameInput}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="Ex: Sneakers Rouge Édition Limitée"
                />
              </div>
              <div>
                <Label className="text-xs">DESCRIPTION</Label>
                <Textarea
                  value={form.description}
                  data-testid={ADMIN.productDescriptionInput}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1.5 rounded-md"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">PRIX (EUR)</Label>
                  <Input
                    value={form.price}
                    data-testid={ADMIN.productPriceInput}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    type="number"
                    step="0.01"
                    className="mt-1.5 h-10 rounded-md font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">SKU</Label>
                  <Input
                    value={form.sku}
                    data-testid={ADMIN.productSkuInput}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    className="mt-1.5 h-10 rounded-md font-mono"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">URL IMAGE</Label>
                <Input
                  value={form.image_url}
                  data-testid={ADMIN.productImageInput}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="https://..."
                />
              </div>

              <div className="border border-zinc-200 rounded-md p-3 space-y-3" data-testid="admin-product-bogo-block">
                <label
                  className="flex items-start gap-3 cursor-pointer"
                  data-testid="admin-product-bogo-toggle"
                >
                  <input
                    type="checkbox"
                    checked={!!form.bogo_enabled}
                    onChange={(e) => setForm({ ...form, bogo_enabled: e.target.checked })}
                    className="mt-0.5 h-4 w-4"
                    data-testid="admin-product-bogo-checkbox"
                  />
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-1.5">🎁 Offre 2ème article</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Active une promotion sur le 2ème article. La remise s'applique automatiquement
                      au checkout et sur la commande Shopify.
                    </div>
                  </div>
                </label>

                {form.bogo_enabled && (
                  <div className="pl-7 space-y-3 pt-1">
                    <div>
                      <Label className="text-xs">TYPE D'OFFRE</Label>
                      <Select
                        value={form.bogo_offer_type}
                        onValueChange={(v) => setForm({ ...form, bogo_offer_type: v })}
                      >
                        <SelectTrigger data-testid="admin-product-bogo-type" className="mt-1.5 h-10 rounded-md">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="second_free">1 acheté = 1 offert (2ème gratuit)</SelectItem>
                          <SelectItem value="second_percent">2ème article à -X%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.bogo_offer_type === 'second_percent' && (
                      <div>
                        <Label className="text-xs">RÉDUCTION SUR LE 2ème (%)</Label>
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          step="1"
                          value={form.bogo_discount_percent}
                          data-testid="admin-product-bogo-percent"
                          onChange={(e) => setForm({ ...form, bogo_discount_percent: e.target.value })}
                          className="mt-1.5 h-10 rounded-md font-mono"
                          placeholder="50"
                        />
                      </div>
                    )}

                    <div>
                      <Label className="text-xs">PORTÉE</Label>
                      <Select
                        value={form.bogo_scope}
                        onValueChange={(v) => setForm({ ...form, bogo_scope: v })}
                      >
                        <SelectTrigger data-testid="admin-product-bogo-scope" className="mt-1.5 h-10 rounded-md">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="same_product">Même produit (2 identiques → 1 récompensé)</SelectItem>
                          <SelectItem value="cheapest_in_cart">Le moins cher du panier (entre produits éligibles)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-zinc-500 mt-1.5">
                        « Le moins cher du panier » regroupe tous les produits éligibles : pour
                        chaque tranche de 2 articles, le moins cher est récompensé.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">SHOPIFY VARIANT ID (pour création commande)</Label>
                <Input
                  value={form.shopify_variant_id}
                  data-testid={ADMIN.productVariantInput}
                  onChange={(e) => setForm({ ...form, shopify_variant_id: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono"
                  placeholder="44218731270"
                />
                <p className="text-xs text-zinc-500 mt-2">Pour lier à un variant existant dans Shopify lors de la création de commande.</p>
              </div>

              <div>
                <Label className="text-xs">SHOPIFY PRODUCT ID (pour snippet vendeur)</Label>
                <Input
                  value={form.shopify_product_id}
                  data-testid={ADMIN.productShopifyIdInput}
                  onChange={(e) => setForm({ ...form, shopify_product_id: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono"
                  placeholder="7234567890123"
                />
                <p className="text-xs text-zinc-500 mt-2">Permet au snippet posé sur Shopify de retrouver ce produit via son ID Shopify.</p>
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Affichage côté vitrine (quand client vient du site vitrine)</div>
                <p className="text-xs text-zinc-500 mb-3">
                  Si rempli, ces 3 champs remplacent l'affichage Shopify ci-dessus quand le client arrive via la route vitrine.
                  Sinon, on retombe sur les valeurs Shopify. Le prix reste identique.
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">NOM VITRINE (vu par client venant du vitrine + sur le reçu)</Label>
                    <Input
                      value={form.vitrine_name}
                      data-testid="admin-product-vitrine-display-name-input"
                      onChange={(e) => setForm({ ...form, vitrine_name: e.target.value })}
                      className="mt-1.5 h-10 rounded-md"
                      placeholder="Ex: Sneakers VS-01 Édition Privée"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">DESCRIPTION VITRINE</Label>
                    <Textarea
                      value={form.vitrine_description}
                      data-testid="admin-product-vitrine-description-input"
                      onChange={(e) => setForm({ ...form, vitrine_description: e.target.value })}
                      className="mt-1.5 rounded-md"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">URL IMAGE VITRINE</Label>
                    <Input
                      value={form.vitrine_image_url}
                      data-testid="admin-product-vitrine-image-input"
                      onChange={(e) => setForm({ ...form, vitrine_image_url: e.target.value })}
                      className="mt-1.5 h-10 rounded-md"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Référence vitrine (metadata Stripe — invisible client)</div>
                <p className="text-xs text-zinc-500 mb-3">
                  URL + ID du produit vitrine équivalent. Envoyés dans les metadata du PaymentIntent
                  pour que la vente apparaisse comme vitrine dans Stripe Dashboard (peu importe d'où vient le client).
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">URL DU PRODUIT VITRINE</Label>
                    <Input
                      value={form.vitrine_url}
                      data-testid={ADMIN.productVitrineUrlInput}
                      onChange={(e) => setForm({ ...form, vitrine_url: e.target.value })}
                      className="mt-1.5 h-10 rounded-md"
                      placeholder="https://mon-site-vitrine.com/produit/..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">ID PRODUIT VITRINE (pour snippet vitrine)</Label>
                    <Input
                      value={form.vitrine_product_id}
                      data-testid={ADMIN.productVitrineIdInput}
                      onChange={(e) => setForm({ ...form, vitrine_product_id: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono"
                      placeholder="VTR-001"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Annuler</Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid={ADMIN.saveProductButton}
                className="btn-primary rounded-md"
              >
                {editing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {platformStores.length === 0 && (
        <Card className="card-flat p-6 mb-6 bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-800">
            Ajoutez d'abord une boutique dans la section "Boutiques" pour pouvoir créer des produits.
          </p>
        </Card>
      )}

      {platformStores.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="products-filter-bar">
          <StoreFilter stores={platformStores} value={storeFilter} onChange={setStoreFilter} testId="products-store-filter" />
          <span className="text-xs text-zinc-500 font-mono" data-testid="products-visible-count">{visible.length} produit(s)</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.length === 0 ? (
          <Card className="card-flat p-10 col-span-full text-center">
            <Package className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
            <h3 className="font-display font-bold mb-1">Aucun produit</h3>
            <p className="text-zinc-500 text-sm">Créez votre premier produit pour générer un snippet de paiement.</p>
          </Card>
        ) : visible.map((p) => (
          <Card key={p.id} className="card-flat overflow-hidden">
            <div className="aspect-square bg-zinc-100 grid-bg flex items-center justify-center">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="h-12 w-12 text-zinc-300" />
              )}
            </div>
            <div className="p-5">
              <div className="text-xs text-zinc-500 font-mono mb-1 truncate">{storeName(p.store_id)}</div>
              <div className="font-display font-bold text-lg tracking-tight truncate">{p.name}</div>
              {p.bogo_enabled && (
                <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`product-bogo-badge-${p.id}`}>
                  🎁 {p.bogo_offer_type === 'second_percent' ? `2ème à -${Math.round(p.bogo_discount_percent || 0)}%` : '1 acheté 1 offert'}
                </span>
              )}
              <div className="mt-1 flex items-center justify-between">
                <span className="font-mono font-bold">{p.price?.toFixed(2)} €</span>
                {p.sku && <span className="text-xs text-zinc-400 font-mono truncate">{p.sku}</span>}
              </div>
              {p.variants?.length > 1 && (
                <button
                  onClick={() => openVariants(p)}
                  data-testid={`product-variants-${p.id}`}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 hover:bg-indigo-100 transition-colors"
                >
                  <Layers className="h-3 w-3" /> {p.variants.length} variantes ({Math.min(...p.variants.map((v) => v.price)).toFixed(2)}–{Math.max(...p.variants.map((v) => v.price)).toFixed(2)} €)
                </button>
              )}
              <div className="mt-4 flex gap-1">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="rounded-md flex-1">
                  <Edit2 className="h-3 w-3 mr-1" /> Modifier
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(p.id)}
                  data-testid={ADMIN.deleteProductButton}
                  className="rounded-md text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Re-import from Shopify dialog */}
      <Dialog open={reimportOpen} onOpenChange={setReimportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <RefreshCw className="h-5 w-5" /> Réimporter les produits
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                Cette action lit le catalogue de la boutique sélectionnée (Shopify ou WooCommerce) et le réinjecte dans l'onglet Produits.
                Choisissez si vous voulez tout effacer avant ré-import, ou conserver les liens vitrine existants.
              </div>
            </div>
            <div>
              <Label className="text-xs">BOUTIQUE</Label>
              <Select value={reimportStoreId} onValueChange={setReimportStoreId}>
                <SelectTrigger data-testid="reimport-store-select" className="mt-1.5 h-10 rounded-md">
                  <SelectValue placeholder="Choisir une boutique" />
                </SelectTrigger>
                <SelectContent>
                  {platformStores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">MODE</Label>
              <div className="mt-1.5 space-y-2">
                <label className="flex items-start gap-2 p-3 border border-zinc-200 rounded-md cursor-pointer hover:bg-zinc-50" data-testid="reimport-mode-wipe">
                  <input
                    type="radio"
                    name="reimport-mode"
                    checked={reimportWipe}
                    onChange={() => setReimportWipe(true)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold">Tout effacer et ré-importer (recommandé)</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Supprime tous les produits actuels de cette boutique, puis recharge depuis la plateforme. Les liaisons vitrine seront perdues — à refaire dans l'onglet Synchro.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 border border-zinc-200 rounded-md cursor-pointer hover:bg-zinc-50" data-testid="reimport-mode-merge">
                  <input
                    type="radio"
                    name="reimport-mode"
                    checked={!reimportWipe}
                    onChange={() => setReimportWipe(false)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold">Mettre à jour sans effacer</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Conserve les produits existants et leurs liaisons vitrine. Met à jour les prix / titres Shopify modifiés, et ajoute les nouveaux produits.</div>
                  </div>
                </label>
              </div>
            </div>

            {reimportResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900" data-testid="reimport-result">
                <div className="font-semibold mb-1">
                  ✓ Réimport terminé
                </div>
                {(reimportResult.per_store || []).map((ps) => (
                  <div key={ps.store_id} className="font-mono">
                    {ps.store_name} : {ps.deleted ? `${ps.deleted} supprimé(s) · ` : ''}{ps.imported} importé(s)
                    {ps.error && <span className="text-red-700"> · ERR: {ps.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setReimportOpen(false)} disabled={reimportRunning}>
              Fermer
            </Button>
            <Button
              onClick={runReimport}
              disabled={reimportRunning || !reimportStoreId}
              data-testid="reimport-confirm-button"
              className="btn-primary rounded-md"
            >
              {reimportRunning ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Réimport en cours…</>
              ) : (
                <>{reimportWipe ? 'Effacer et ré-importer' : 'Mettre à jour'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk vitrine-name editor */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Tags className="h-5 w-5" /> Noms vitrine — édition en masse
            </DialogTitle>
          </DialogHeader>
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-600 mb-3">
            Renseignez le <strong>nom vitrine</strong> de chaque produit : c'est ce nom (et l'ID) qui est envoyé
            dans les metadata Stripe — jamais le nom Shopify. Les lignes en orange n'ont pas encore de nom vitrine
            (Stripe affichera « Article » par défaut).
          </div>
          <div className="max-h-[55vh] overflow-auto -mx-1 px-1">
            <table className="w-full text-sm" data-testid="bulk-vitrine-table">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs text-zinc-400 uppercase tracking-wider border-b border-zinc-100">
                  <th className="py-2 pr-3 font-medium">Produit (Shopify)</th>
                  <th className="py-2 pr-3 font-medium">Nom vitrine</th>
                  <th className="py-2 pr-3 font-medium">URL vitrine</th>
                  <th className="py-2 font-medium">ID vitrine</th>
                </tr>
              </thead>
              <tbody>
                {bulkRows.map((r, idx) => (
                  <tr
                    key={r.id}
                    className={`border-b border-zinc-50 ${r.vitrine_name.trim() ? '' : 'bg-amber-50/60'}`}
                    data-testid="bulk-vitrine-row"
                  >
                    <td className="py-2 pr-3 align-middle">
                      <div className="font-medium text-zinc-700 truncate max-w-[160px]" title={r.name}>{r.name}</div>
                      <div className="text-[11px] text-zinc-400 font-mono">{storeName(r.store_id)}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <Input
                        value={r.vitrine_name}
                        onChange={(e) => updateBulkRow(idx, 'vitrine_name', e.target.value)}
                        placeholder="Nom vitrine"
                        className="h-9 rounded-md text-sm w-44"
                        data-testid={`bulk-vitrine-name-${idx}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <Input
                        value={r.vitrine_url}
                        onChange={(e) => updateBulkRow(idx, 'vitrine_url', e.target.value)}
                        placeholder="https://…"
                        className="h-9 rounded-md text-sm w-44"
                      />
                    </td>
                    <td className="py-2">
                      <Input
                        value={r.vitrine_product_id}
                        onChange={(e) => updateBulkRow(idx, 'vitrine_product_id', e.target.value)}
                        placeholder="VTR-001"
                        className="h-9 rounded-md text-sm font-mono w-32"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setBulkOpen(false)} disabled={bulkSaving}>
              Annuler
            </Button>
            <Button onClick={saveBulk} disabled={bulkSaving} className="btn-primary rounded-md" data-testid="bulk-vitrine-save">
              {bulkSaving ? 'Enregistrement…' : 'Enregistrer tout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Per-variant vitrine editor */}
      <Dialog open={!!variantsProduct} onOpenChange={(o) => !o && setVariantsProduct(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Layers className="h-5 w-5" /> Variantes — {variantsProduct?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-600 mb-3">
            Chaque variante a son propre prix. Renseigne le <strong>nom vitrine</strong> (et l'URL/image) par variante :
            au checkout, c'est le prix et le nom vitrine de la variante choisie par le client qui sont utilisés.
          </div>
          <div className="max-h-[55vh] overflow-auto -mx-1 px-1">
            <table className="w-full text-sm" data-testid="variants-table">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs text-zinc-400 uppercase tracking-wider border-b border-zinc-100">
                  <th className="py-2 pr-3 font-medium">Variante</th>
                  <th className="py-2 pr-3 font-medium">Prix</th>
                  <th className="py-2 pr-3 font-medium">Nom vitrine</th>
                  <th className="py-2 pr-3 font-medium">URL vitrine</th>
                  <th className="py-2 font-medium">Image vitrine</th>
                </tr>
              </thead>
              <tbody>
                {variantRows.map((r, idx) => (
                  <tr key={r.variant_id || idx} className={`border-b border-zinc-50 ${r.vitrine_name.trim() ? '' : 'bg-amber-50/60'}`} data-testid="variant-row">
                    <td className="py-2 pr-3 align-middle">
                      <div className="font-medium text-zinc-700 truncate max-w-[140px]" title={r.title}>{r.title || '—'}</div>
                      <div className="text-[11px] text-zinc-400 font-mono">{r.sku || '—'}</div>
                    </td>
                    <td className="py-2 pr-3 font-mono font-bold whitespace-nowrap">{Number(r.price).toFixed(2)} €</td>
                    <td className="py-2 pr-3">
                      <Input value={r.vitrine_name} onChange={(e) => updateVariantRow(idx, 'vitrine_name', e.target.value)} placeholder="Nom vitrine" className="h-9 rounded-md text-sm w-40" data-testid={`variant-vitrine-name-${idx}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <Input value={r.vitrine_url} onChange={(e) => updateVariantRow(idx, 'vitrine_url', e.target.value)} placeholder="https://…" className="h-9 rounded-md text-sm w-40" />
                    </td>
                    <td className="py-2">
                      <Input value={r.vitrine_image_url} onChange={(e) => updateVariantRow(idx, 'vitrine_image_url', e.target.value)} placeholder="https://… image" className="h-9 rounded-md text-sm w-40" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setVariantsProduct(null)} disabled={variantsSaving}>Annuler</Button>
            <Button onClick={saveVariants} disabled={variantsSaving} className="btn-primary rounded-md" data-testid="variants-save">
              {variantsSaving ? 'Enregistrement…' : 'Enregistrer les variantes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
